import { usePocQuery } from 'core/custom-hooks/use-poc-query.hook';
import { deviceKeys } from 'core/query-client/keys/device.keys';
import { executeRequest } from 'core/utils/backend-service';
import { DeviceTypeDetails } from 'modules/devices/domain/api/device-type.api';
import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { fetchDeviceTypesFromADM } from '../api/device-types.api';
import { DeviceTypesContext, DeviceTypesContextType } from '../device-types.context';
import { DEVICE_TYPE_SOURCE } from '../device-types.constants';
import { DeviceTypeSource, FetchDeviceTypesStrategy } from '../device-types.type';

export type DeviceTypesProviderProps = PropsWithChildren<
    | {
          fetchStrategy?: never;
          source?: typeof DEVICE_TYPE_SOURCE.ADM;
      }
    | {
          fetchStrategy: FetchDeviceTypesStrategy;
          source: DeviceTypeSource;
      }
>;

export const DeviceTypesProvider: React.FC<DeviceTypesProviderProps> = ({
    children,
    fetchStrategy = fetchDeviceTypesFromADM,
    source = DEVICE_TYPE_SOURCE.ADM,
}) => {
    const [deviceTypes, setDeviceTypes] = useState<DeviceTypeDetails[]>([]);

    const { data, isFetching } = usePocQuery<DeviceTypeDetails[]>({
        queryKey: deviceKeys.types(source),
        queryFn: () => executeRequest(() => fetchStrategy().then((response) => response.result.deviceTypes)),
    });

    // Mirror the query result into local state so consumers can apply optimistic updates
    // (e.g. settings device-types rollback) through setDeviceTypes until they migrate too.
    useEffect(() => {
        if (data) {
            setDeviceTypes(data);
        }
    }, [data]);

    const value: DeviceTypesContextType = useMemo(
        () => ({
            deviceTypes,
            setDeviceTypes,
            isLoading: isFetching,
        }),
        [deviceTypes, isFetching]
    );

    return <DeviceTypesContext.Provider value={value}>{children}</DeviceTypesContext.Provider>;
};
